import { DateTime } from "luxon";
import type { Dispatch, SetStateAction } from "react";
import { localStorageService } from "../services/LocalStorageService";
import { floorToIndex } from "./utilFunctions";

export type SortOrder = "asc" | "desc";
export type SortingState<T> = {
	sortColumns: Array<keyof T>;
	sortOrder: SortOrder;
	label?: string;
	// Row-level comparator. When set, the per-column `compareValues` walk is skipped for this entry
	// and `sortColumns` becomes purely identity (matched by header clicks / popup lookup).
	customSortFunction?: (a: T, b: T) => 1 | 0 | -1;
	// When true, null/undefined values are pinned to the END of this entry's order regardless of
	// `sortOrder` — i.e. they stay last in both asc and desc, instead of flipping to the front on desc.
	// Also honored alongside `customSortFunction`: rows whose `sortColumns` value is nullish are pinned
	// first, and the custom comparator only orders the rest (it can't do this itself — it never sees
	// `sortOrder`, and its result is negated on desc).
	sortNullUndefinedLast?: boolean;
};

/**
 * Caller-supplied catalog entry for a sortable axis. Used by `useMultiSort` to enrich
 * default/persisted `SortingState` entries with the option's `label` and `customSortFunction`,
 * and by the sort popup / clickable headers to produce matching sort entries on click.
 */
export type MultiSortOption<T> = {
	label: string;
	sortColumns?: Array<keyof T>;
	customSortFunction?: (a: T, b: T) => 1 | 0 | -1;
	sortNullUndefinedLast?: boolean;
};

/** Same columns in the same order — the only way two tuples are the same sort key. */
export function sortColumnsEqual<T>(a: Array<keyof T>, b: Array<keyof T>): boolean {
	return a.length === b.length && a.every((col, index) => col === b[index]);
}

/**
 * Returns the option whose `sortColumns` match the entry's `sortColumns` exactly
 * (same length, same elements in order). Used by `useMultiSort` and SystemDataGrid to
 * re-attach `label` + `customSortFunction` after localStorage round-trips drop them.
 */
export function findMatchingSortOption<T>(cols: Array<keyof T>, options: MultiSortOption<T>[] | undefined): MultiSortOption<T> | undefined {
	return options?.find((option) => option.sortColumns && sortColumnsEqual(option.sortColumns, cols));
}

/**
 * Drops the entries whose columns match none of `options`. For a grid whose offered sort axes change
 * (a column offered only in some modes): a sort on an axis it no longer offers would keep ordering
 * the rows with no row in the sort popup to see or clear it. Not for a grid that keeps an unlisted
 * tie-breaker entry on purpose (the sort popup's hidden entries) — this would drop it.
 */
export function keepOfferedSortEntries<T>(entries: SortingState<T>[], options: MultiSortOption<T>[]): SortingState<T>[] {
	return entries.filter((entry) => findMatchingSortOption(entry.sortColumns, options) !== undefined);
}

/**
 * Returns the entry enriched with `label` and `customSortFunction` from the matching option.
 * Existing fields on the entry win; option fields fill in any holes.
 */
export function enrichSortStateFromOptions<T>(entries: SortingState<T>[], options: MultiSortOption<T>[] | undefined): SortingState<T>[] {
	if (!options) return entries;
	return entries.map((entry) => {
		const matched = findMatchingSortOption(entry.sortColumns, options);
		if (!matched) return entry;
		return {
			...entry,
			label: entry.label ?? matched.label,
			customSortFunction: entry.customSortFunction ?? matched.customSortFunction,
		};
	});
}

/**
 * Re-applies each option's `sortNullUndefinedLast` to the entries that sort on THAT option's columns.
 *
 * Scoped per entry, never grid-wide: the flag belongs to the axis that declared it, so registering a
 * nulls-last column must not change how an UNRELATED column orders its blanks. Applied at sort time
 * rather than trusted on the entry because the two ways an entry is created — a header click and the
 * sort popup, both via {@link handleColumnSortClick} — don't carry the flag, and neither do entries
 * persisted to localStorage before an option gained it.
 */
export function applyNullOrderingFromOptions<T>(entries: SortingState<T>[], options: MultiSortOption<T>[] | undefined): SortingState<T>[] {
	if (!options) return entries;
	return entries.map((entry) =>
		findMatchingSortOption(entry.sortColumns, options)?.sortNullUndefinedLast ? { ...entry, sortNullUndefinedLast: true } : entry,
	);
}

const collator = new Intl.Collator(undefined, {
	numeric: true, // "2" < "10"
	sensitivity: "base", // case/diacritics-insensitive; tweak if you need strict
});

type Comparator = (a: unknown, b: unknown, customStringOrder?: Map<string, number> | null) => number;

const undefinedCmp: Comparator = (a, b) => {
	if (a === undefined && b === undefined) return 0;
	if (a === undefined) return 1;
	if (b === undefined) return -1;
	return 0;
};

const nullCmp: Comparator = (a, b) => {
	if (a === null && b === null) return 0;
	if (a === null) return 1;
	if (b === null) return -1;
	return 0;
};

const numberCmp: Comparator = (a, b) => {
	if (typeof a !== "number" || typeof b !== "number") return 0;
	const aNaN = Number.isNaN(a);
	const bNaN = Number.isNaN(b);
	if (aNaN && bNaN) return 0;
	if (aNaN) return 1;
	if (bNaN) return -1;
	return a - b;
};

const booleanCmp: Comparator = (a, b) => {
	if (typeof a !== "boolean" || typeof b !== "boolean") return 0;
	if (a === b) return 0;
	return a ? 1 : -1;
};

const dateCmp: Comparator = (a, b) => {
	if (!DateTime.isDateTime(a) || !DateTime.isDateTime(b)) return 0;
	if (!a.isValid || !b.isValid) {
		return a.isValid ? 1 : -1;
	}
	const aMs = a.toMillis();
	const bMs = b.toMillis();
	if (aMs === null || bMs === null) return 0;
	return aMs < bMs ? -1 : aMs > bMs ? 1 : 0;
};

const emptyStringCmp: Comparator = (a, b) => {
	if (typeof a !== "string" || typeof b !== "string") return 0;
	// Empty strings sort after non-empty
	const aEmpty = a.length === 0;
	const bEmpty = b.length === 0;
	if (aEmpty && bEmpty) return 0;
	if (aEmpty) return 1;
	if (bEmpty) return -1;
	return 0;
};

const customStringOrderCmp: Comparator = (a, b, customStringOrder) => {
	if (typeof a !== "string" || typeof b !== "string" || !customStringOrder) return 0;
	const idxA = customStringOrder.get(a);
	const idxB = customStringOrder.get(b);
	if (idxA !== undefined && idxB !== undefined) return idxA - idxB;
	if (idxA !== undefined) return -1;
	if (idxB !== undefined) return 1;
	return 0;
};

const floorToIndexStringCmp: Comparator = (a, b) => {
	if (typeof a !== "string" || typeof b !== "string") return 0;
	const floorA = floorToIndex(a);
	const floorB = floorToIndex(b);
	if (floorA !== undefined && floorB !== undefined) return floorA - floorB;
	if (floorA !== undefined) return -1;
	if (floorB !== undefined) return 1;
	return 0;
};

const stringCmp: Comparator = (a, b) => {
	if (typeof a !== "string" || typeof b !== "string") return 0;
	return collator.compare(a, b);
};

/** Cross-type fallback via JS operator coercion. Strings are already handled by {@link stringCmp}. */
const fallbackCmp: Comparator = (a, b) => {
	if (typeof a === "string" && typeof b === "string") return 0; /*Strings should have been handled outside of this function!*/
	if ((a as any) > (b as any)) return 1;
	if ((a as any) < (b as any)) return -1;
	return 0;
};

const COMPARATORS: readonly Comparator[] = [
	undefinedCmp,
	nullCmp,
	numberCmp,
	booleanCmp,
	dateCmp,
	emptyStringCmp,
	customStringOrderCmp,
	floorToIndexStringCmp,
	stringCmp,
	fallbackCmp,
];

/**
 * Compares two values for sorting purposes, handling various data types and edge cases.
 * Supports undefined, null, numbers (including NaN), booleans, dates (and date-like objects), and strings.
 *
 * For string inputs the chain is: customStringOrder → floorToIndex → locale compare.
 * Prefer this over hand-rolled `floorToIndex` + `localeCompare` blocks at call sites.
 *
 * @param a - first value to compare
 * @param b - second value to compare
 * @param customStringOrder - optional map defining a custom order for specific string values
 */
export function compareValues(a: unknown, b: unknown, customStringOrder?: Map<string, number> | null): number {
	return COMPARATORS.reduce((acc, fn) => acc || fn(a, b, customStringOrder), 0);
}

/**
 * Location names in the app's location order: the user's manual order when set, then floor index, then name — blank
 * names last. A copy; the input is left as it was.
 */
export const sortByLocationOrder = (locationNames: readonly string[], customLocationOrder: Map<string, number> | null): string[] =>
	[...locationNames].sort((a, b) => compareValues(a, b, customLocationOrder));

/**
 * Creates a comparator function for multi-column sorting based on the provided sort state.
 * @param sortState - An array of sorting states, each containing columns to sort by and the sort order.
 * @param customStringOrder - Optional map defining a custom order for specific string values.
 * @returns A comparator function that can be used in array sorting.
 */
const isNullish = (v: unknown): boolean => v === null || v === undefined;

/** Pins a row whose value in any of `sortColumns` is nullish after one whose value is defined — never
 * negated, so the pinned rows stay last in both directions. 0 when both or neither are nullish. */
function compareNullishLast<T>(a: T, b: T, sortColumns: Array<keyof T>): 1 | 0 | -1 {
	for (const sortColumn of sortColumns) {
		const aNullish = isNullish(a[sortColumn]);
		if (aNullish !== isNullish(b[sortColumn])) return aNullish ? 1 : -1;
	}
	return 0;
}

export function multiSortComparator<T>(sortState: SortingState<T>[], customStringOrder?: Map<string, number> | null) {
	return (a: T, b: T) => {
		for (const { sortColumns, sortOrder, customSortFunction, sortNullUndefinedLast } of sortState) {
			if (customSortFunction) {
				if (sortNullUndefinedLast) {
					const nullishCmp = compareNullishLast(a, b, sortColumns);
					if (nullishCmp !== 0) return nullishCmp;
				}
				const cmpValue = customSortFunction(a, b);
				if (cmpValue !== 0) return sortOrder === "asc" ? cmpValue : -cmpValue;
				continue;
			}
			for (const sortColumn of sortColumns) {
				const aVal = a[sortColumn];
				const bVal = b[sortColumn];
				if (sortNullUndefinedLast && (isNullish(aVal) || isNullish(bVal))) {
					// Decide nullish ordering OUTSIDE the `sortOrder` negation so null/undefined stay last in
					// both directions. `compareValues` already ranks nullish after defined (and null before undefined).
					const nullishCmp = compareValues(aVal, bVal, customStringOrder);
					if (nullishCmp !== 0) return nullishCmp;
					continue;
				}
				const cmpValue = compareValues(aVal, bVal, customStringOrder);
				if (cmpValue !== 0) return sortOrder === "asc" ? cmpValue : -cmpValue;
			}
		}
		return 0; // all equal
	};
}

export function handleSavingSortingStateToLocalStorage<T>(sortState: SortingState<T>[], localStorageTableKey?: string, userId?: string) {
	if (localStorageTableKey && userId) {
		localStorageService.saveTableSortingState(localStorageTableKey, sortState, userId);
	}
}

/**
 * Header-click sorting for grids that opt out of key stacking.
 *
 * `retainEntry` is the load-bearing argument. Grids park comparators in sort state that have no
 * header and no popup row to rebuild them from — Ops Backoffice keeps a `stage` tie-breaker that is
 * deliberately absent from its `sortingOptions`, and Master Schedule ships a two-entry default. A
 * replace that just cleared the array would drop those silently and permanently reorder the grid
 * with no affordance to get it back, so entries `retainEntry` approves survive every replace.
 */
function handleNonStackingSortClick<T>({
	sortState,
	setSortState,
	columns,
	label,
	customSortFunction,
	clickBehavior,
	retainEntry,
	localStorageTableKey,
	userId,
}: {
	sortState: SortingState<T>[];
	setSortState: Dispatch<SetStateAction<SortingState<T>[]>>;
	columns: Array<keyof T>;
	label?: string;
	customSortFunction?: (a: T, b: T) => 1 | 0 | -1;
	clickBehavior: Exclude<SortClickBehavior, "stack">;
	retainEntry?: (entry: SortingState<T>) => boolean;
	localStorageTableKey?: string;
	userId?: string;
}) {
	// Exact tuple, not containment: `every(includes)` also matched a DIFFERENT sort whose columns are a
	// subset of the clicked ones, so clicking [lastName, firstName] rewrote an existing [lastName] entry.
	const matchesClicked = (sState: SortingState<T>) => sortColumnsEqual(sState.sortColumns, columns);
	const foundState = sortState.find(matchesClicked);
	// Position 0 is the primary key. Clicking the column that already holds it means "flip this
	// direction"; clicking any other column means "sort by that instead".
	const clickedIsPrimary = sortState.length > 0 && matchesClicked(sortState[0]);

	if (foundState && (clickBehavior === "append" || clickedIsPrimary)) {
		const nextOrder: SortOrder = foundState.sortOrder === "asc" ? "desc" : "asc";
		// Spread so future-added optional fields on SortingState survive the toggle.
		const entry: SortingState<T> = { ...foundState, sortColumns: columns, sortOrder: nextOrder, label, customSortFunction };
		setSortState((prevState) => {
			const result = Array.from(prevState);
			const indexFound = result.findIndex(matchesClicked);
			result[indexFound] = entry;
			handleSavingSortingStateToLocalStorage(result, localStorageTableKey, userId);
			return result;
		});
		return;
	}

	const newEntry: SortingState<T> = { sortColumns: columns, sortOrder: "asc", label, customSortFunction };

	if (clickBehavior === "append") {
		setSortState((prevState) => {
			const result = [...prevState, newEntry];
			handleSavingSortingStateToLocalStorage(result, localStorageTableKey, userId);
			return result;
		});
		return;
	}

	setSortState((prevState) => {
		const retained = retainEntry ? prevState.filter((entry) => !matchesClicked(entry) && retainEntry(entry)) : [];
		const result = [newEntry, ...retained];
		handleSavingSortingStateToLocalStorage(result, localStorageTableKey, userId);
		return result;
	});
}

/**
 * Sets a column's sort to an EXPLICIT direction, rather than toggling whatever is there.
 *
 * Header clicks toggle, because that is what a header is for. A menu item that says "Sort
 * descending" has to mean descending on the first click, so it needs its own entry point.
 */
export function applyColumnSort<T>({
	sortState,
	setSortState,
	columns,
	label,
	customSortFunction,
	sortOrder,
	clickBehavior = "stack",
	retainEntry,
	localStorageDetails,
}: {
	sortState: SortingState<T>[];
	setSortState: Dispatch<SetStateAction<SortingState<T>[]>>;
	columns: Array<keyof T>;
	label?: string;
	customSortFunction?: (a: T, b: T) => 1 | 0 | -1;
	sortOrder: SortOrder;
	clickBehavior?: SortClickBehavior;
	retainEntry?: (entry: SortingState<T>) => boolean;
	localStorageDetails?: { localStorageTableKey: string; userId: string };
}) {
	const { localStorageTableKey, userId } = localStorageDetails ?? { localStorageTableKey: undefined, userId: undefined };
	// Exact tuple, not containment: `every(includes)` also matched a DIFFERENT sort whose columns are a
	// subset of the clicked ones, so clicking [lastName, firstName] rewrote an existing [lastName] entry.
	const matchesClicked = (sState: SortingState<T>) => sortColumnsEqual(sState.sortColumns, columns);
	const existing = sortState.find(matchesClicked);
	const entry: SortingState<T> = { ...existing, sortColumns: columns, sortOrder, label, customSortFunction };

	setSortState((prevState) => {
		const indexFound = prevState.findIndex(matchesClicked);
		// Already in the sort: change direction where it stands, so a menu action never silently
		// re-prioritizes the other keys.
		if (indexFound >= 0) {
			const result = Array.from(prevState);
			result[indexFound] = entry;
			handleSavingSortingStateToLocalStorage(result, localStorageTableKey, userId);
			return result;
		}
		const retained = clickBehavior === "replace" ? prevState.filter((e) => (retainEntry ? retainEntry(e) : false)) : prevState;
		const result = [entry, ...retained];
		handleSavingSortingStateToLocalStorage(result, localStorageTableKey, userId);
		return result;
	});
}

/**
 * How a click resolves against the sort keys already in state.
 *
 * - `stack` — the historical behavior: an unsorted column is inserted as a NEW primary key and
 *   everything already there is demoted. Three clicks on three headers leave a 3-key sort.
 * - `replace` — the clicked column becomes the whole sort. What most people expect from a header.
 * - `append` — the clicked column joins as the lowest-priority tie-breaker (shift+click).
 */
export type SortClickBehavior = "stack" | "replace" | "append";

/**
 * Handles the click event for sorting columns in a table.
 * @param sortState
 * @param setSortState
 * @param columns - which sorting columns were clicked
 * @param allowRemovalWhenDescendingToggle - if true, clicking a descending column will remove it from the sort state; if false, it will toggle back to ascending
 * @param label - optional label for the sorting state
 * @param appendToStartIfAlreadyExists - if true, edited sorting states are added to the start of the list; if false, they are added to the end
 * @param localStorageDetails - optional details for saving the sorting state to local storage
 * @param clickBehavior - see {@link SortClickBehavior}. Defaults to `stack` so existing callers are unaffected.
 * @param retainEntry - `replace` only: entries this keeps are never dropped. See {@link handleNonStackingSortClick}.
 */
export function handleColumnSortClick<T>({
	setSortState,
	columns,
	sortState,
	allowRemovalWhenDescendingToggle = false,
	appendToStartIfAlreadyExists = false,
	label,
	customSortFunction,
	localStorageDetails,
	clickBehavior = "stack",
	retainEntry,
}: {
	sortState: SortingState<T>[];
	setSortState: Dispatch<SetStateAction<SortingState<T>[]>>;
	columns?: Array<keyof T>;
	label?: string;
	allowRemovalWhenDescendingToggle?: boolean;
	appendToStartIfAlreadyExists?: boolean;
	customSortFunction?: (a: T, b: T) => 1 | 0 | -1;
	localStorageDetails?: {
		localStorageTableKey: string;
		userId: string;
	};
	clickBehavior?: SortClickBehavior;
	retainEntry?: (entry: SortingState<T>) => boolean;
}) {
	const { localStorageTableKey, userId } = localStorageDetails || { localStorageTableKey: undefined, userId: undefined };
	if (!columns) return;

	if (clickBehavior !== "stack") {
		handleNonStackingSortClick({
			sortState,
			setSortState,
			columns,
			label,
			customSortFunction,
			clickBehavior,
			retainEntry,
			localStorageTableKey,
			userId,
		});
		return;
	}

	const matchesClicked = (state: SortingState<T>) => sortColumnsEqual(state.sortColumns, columns);
	const foundState = sortState.find(matchesClicked);

	if (foundState) {
		// Toggle the sort order for the existing column
		const nextOrder: SortOrder = foundState.sortOrder === "asc" ? "desc" : "asc";

		if (nextOrder === "asc" && allowRemovalWhenDescendingToggle) {
			setSortState((prevState) => {
				const result = prevState.filter((state) => !matchesClicked(state));
				handleSavingSortingStateToLocalStorage(result, localStorageTableKey, userId);
				return result;
			});
			return;
		}

		/**
		 * Spread `foundState` so any future-added optional fields on {@link SortingState} survive the toggle.
		 * The explicit overrides below are the only fields this function is responsible for.
		 */
		const entry: SortingState<T> = {
			...foundState,
			sortColumns: columns,
			sortOrder: nextOrder,
			label,
			customSortFunction,
		};
		setSortState((prevState) => {
			if (!appendToStartIfAlreadyExists) {
				const indexFound = prevState.findIndex(matchesClicked);
				prevState[indexFound] = entry;
				handleSavingSortingStateToLocalStorage(prevState, localStorageTableKey, userId);
				return Array.from(prevState);
			}
			const result = [entry, ...prevState.filter((state) => !matchesClicked(state))];
			handleSavingSortingStateToLocalStorage(result, localStorageTableKey, userId);
			return result;
		});
		return;
	}

	// Add the new column with ascending order
	const newEntry: SortingState<T> = {
		sortColumns: columns,
		sortOrder: "asc",
		label,
		customSortFunction,
	};
	setSortState((prevState) => {
		const result = [newEntry, ...prevState];
		handleSavingSortingStateToLocalStorage(result, localStorageTableKey, userId);
		return result;
	});
}
